import { customElement, scss } from '../../core/ids-decorators';
import { attributes, htmlAttributes } from '../../core/ids-attributes';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import { sizes } from '../ids-icon/ids-icon-attributes';
import { IdsColorValue } from '../../utils/ids-color-utils/ids-color-utils';

import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsTooltipMixin from '../../mixins/ids-tooltip-mixin/ids-tooltip-mixin';

import IdsElement from '../../core/ids-element';

import '../ids-icon/ids-icon';
import '../ids-text/ids-text';
import styles from './ids-alert.scss';

export type IdsAlertTypes = 'success' | 'info' | 'warning' | 'error' | 'in-progress' | 'new' | 'caution' | 'alert';

const Base = IdsTooltipMixin(
  IdsEventsMixin(
    IdsElement
  )
);

/**
 * IDS Alert Component
 * @type {IdsAlert}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 * @mixes IdsTooltipMixin
 * @part icon - the icon element
 * @part label - the label text element
 */
@customElement('ids-alert')
@scss(styles)
export default class IdsAlert extends Base {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
  }

  /**
   * Set the tooltip target to the icon element only
   * @returns {HTMLElement | SVGElement} The icon element
   */
  onTooltipTargetDetection(): HTMLElement | SVGElement {
    return this.shadowRoot?.querySelector('ids-icon') as HTMLElement || this;
  }

  /**
   * Set the alert color on the tooltip (mixin function)
   * @param {any} tooltip the tooltip element
   */
  beforeTooltipShow(tooltip?: any) {
    if (tooltip.popup) {
      const hasColor = this.toolTipTarget.getAttribute('color');
      tooltip.popup?.container?.classList.add(`${hasColor || this.toolTipTarget.getAttribute('icon')}-color`);
      tooltip.popup.onPlace = (popupRect: DOMRect) => {
        const tooltipInstane = tooltip;
        // deduct more when tooltip shows on bottom instead on top
        if (tooltipInstane?.popup?.arrowEl?.classList?.contains?.('bottom')
          && tooltipInstane?.popup?.align === 'top') {
          popupRect.y -= 6;
        } else {
          popupRect.y -= 4;
        }

        return popupRect;
      };
    }
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The propertires in an array
   */
  static get attributes() {
    return [
      ...super.attributes,
      attributes.COLOR,
      attributes.DISABLED,
      attributes.ICON,
      attributes.LABEL,
      attributes.LABEL_COLOR,
      attributes.TOOLTIP,
      attributes.SIZE,
      htmlAttributes.ARIA_LABEL
    ];
  }

  /**
   * Create the Template for the contents
   * @returns {string} The template
   */
  template(): string {
    const cssClass = stringToBool(this.disabled) ? ' class="disabled"' : '';
    const iconName = this.icon && ['warning', 'caution'].includes(this.icon) ? 'alert' : this.icon;
    const iconAttr = iconName ? ` icon="${iconName}"` : '';
    const colorAttr = !this.color && this.icon && ['success', 'info', 'warning', 'error', 'in-progress', 'new', 'caution', 'alert'].includes(this.icon)
      ? ` color="${this.icon}"` : '';
    const labelColor = this.labelColor ? ` color="${this.labelColor}"` : '';
    const hidden = !this.label ? ' hidden' : '';
    return `<ids-icon size="${this.size}"${cssClass}${iconAttr}${colorAttr} part="icon"></ids-icon><ids-text class="label-text" part="label"${labelColor}${hidden}>${this.label || ''}</ids-text>`;
  }

  /**
   * Set the alert color
   * @param {IdsColorValue} value The color to use between: error, success, info, ect..
   */
  set color(value: IdsColorValue) {
    if (value) {
      this.setAttribute(attributes.COLOR, value);
      this.shadowRoot?.querySelector('ids-icon')?.setAttribute(attributes.COLOR, value);
    } else {
      this.removeAttribute(attributes.COLOR);
      this.shadowRoot?.querySelector('ids-icon')?.removeAttribute(attributes.COLOR);
    }
  }

  get color(): IdsColorValue {
    return this.getAttribute(attributes.COLOR) as IdsColorValue;
  }

  /**
   * Sets the disabled state
   * @param {boolean | string} value true/false to set disabled/enabled
   */
  set disabled(value: boolean | string) {
    const icon = this.shadowRoot?.querySelector('ids-icon');
    const val = stringToBool(value);
    const validIcons = new Set<IdsAlertTypes>(['success', 'info', 'warning', 'error', 'in-progress', 'new', 'caution', 'alert']);
    if (val) {
      this.setAttribute(attributes.DISABLED, val.toString());
      if (!validIcons.has(this.icon as IdsAlertTypes)) {
        icon?.classList.add('custom-disabled');
      } else {
        icon?.classList.add(attributes.DISABLED);
      }
    } else {
      this.removeAttribute(attributes.DISABLED);
      icon?.classList.remove(attributes.DISABLED, 'custom-disabled');
    }
  }

  get disabled(): boolean | string {
    return stringToBool(this.getAttribute(attributes.DISABLED));
  }

  /**
   * Return the icon of the alert.
   * @returns {string | null} the path data
   */
  get icon(): string | null { return this.getAttribute(attributes.ICON); }

  /**
   * Set the icon
   * @param {string | null} value The Icon Type [success, info, error, warning]
   */
  set icon(value: string | null) {
    const iconElem = this.shadowRoot?.querySelector('ids-icon');
    if (value) {
      this.setAttribute(attributes.ICON, value);
      const iconName = ['warning', 'caution'].includes(value) ? 'alert' : value;
      iconElem?.setAttribute(attributes.ICON, iconName);
      if (!this.color && ['success', 'info', 'warning', 'error', 'in-progress', 'new', 'caution', 'alert'].includes(value)) {
        iconElem?.setAttribute(attributes.COLOR, value);
      }
    } else {
      this.removeAttribute(attributes.ICON);
      iconElem?.removeAttribute(attributes.ICON);
      iconElem?.removeAttribute(attributes.COLOR);
    }
  }

  /**
   * Set the size. May be large, normal/medium or small
   * @param {string} value the size
   */
  set size(value: string | null) {
    if (value && sizes[value]) {
      this.setAttribute(attributes.SIZE, value);
      this.shadowRoot?.querySelector('ids-icon')?.setAttribute(attributes.SIZE, value);
    } else {
      this.removeAttribute(attributes.SIZE);
      this.shadowRoot?.querySelector('ids-icon')?.removeAttribute(attributes.SIZE);
    }
  }

  get size(): string { return this.getAttribute(attributes.SIZE) || 'normal'; }

  #updatingLabel = false;

  /**
   * Set the label text
   * @param {string | null} value The label text
   */
  set label(value: string | null) {
    // Guard flag prevents recursive updates when setting textContent triggers observers
    if (this.#updatingLabel) return;
    this.#updatingLabel = true;

    const labelEl = this.shadowRoot?.querySelector<HTMLElement>('.label-text');
    if (value) {
      this.setAttribute(attributes.LABEL, value);
      if (labelEl) {
        labelEl.textContent = value;
        labelEl.removeAttribute('hidden');
      }
    } else {
      this.removeAttribute(attributes.LABEL);
      if (labelEl) {
        labelEl.textContent = '';
        labelEl.setAttribute('hidden', '');
      }
    }

    this.#updatingLabel = false;
  }

  get label(): string { return this.getAttribute(attributes.LABEL) || ''; }

  /**
   * Set the label color
   * @param {IdsColorValue} value The color to use for the label
   */
  set labelColor(value: IdsColorValue) {
    if (value) {
      this.setAttribute(attributes.LABEL_COLOR, value);
      this.shadowRoot?.querySelector('.label-text')?.setAttribute(attributes.COLOR, value);
    } else {
      this.removeAttribute(attributes.LABEL_COLOR);
      this.shadowRoot?.querySelector('.label-text')?.removeAttribute(attributes.COLOR);
    }
  }

  get labelColor(): IdsColorValue {
    return this.getAttribute(attributes.LABEL_COLOR) as IdsColorValue;
  }

  /**
   * Set the aria-label for accessibility
   * @param {string | null} value The aria-label text
   */
  set ariaLabel(value: string | null) {
    if (value) {
      this.setAttribute(htmlAttributes.ARIA_LABEL, value);
    } else {
      this.removeAttribute(htmlAttributes.ARIA_LABEL);
    }
  }

  /**
   * Get the aria-label
   * @returns {string | null} The aria-label text
   */
  get ariaLabel(): string | null {
    return this.getAttribute(htmlAttributes.ARIA_LABEL);
  }
}
